// @flow
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { intlShape } from 'react-intl';

import TileGrid from '../../../../common/tile-grid/TileGridComponent';
import TileGridContent from '../../../../common/tile-grid-content/TileGridContentComponent';

import {
  getProductTypeIds,
  getProductTypeIntlPluralKey,
  isProductTypeAvailable,
  getFullUploadProductTypeIds,
} from '../../../../services/product-type/ProductTypeService';
import { getProductRouteById } from '../../../../services/products-route/ProductsRouteService';
import { envUrl } from '../../../../services/environment/EnvironmentService';

import type { ProductSizes } from '../../../../../api/types/ProductSizes';

import './ProductSelectionTiles.scss';

export type ProductSelectionTilesProps = {
  config: {
    productSizesAvailable: ProductSizes,
  },
  creationCategory: string,
};

const productTypeImageExtension = 'png';

const validateProductSizesMap = ({ productSizesAvailable }: ProductSizes) => {
  if (productSizesAvailable && typeof productSizesAvailable === 'object') {
    const isValidMap = Object.keys(productSizesAvailable).every((productSizeData) => {
      const dataKey = productSizeData;
      const dataValue = productSizesAvailable[dataKey];
      return typeof dataKey === 'string' && typeof dataValue === 'object';
    });
    return isValidMap ? null : new Error('productSizesAvailable is not a valid map');
  }
  return null;
};

/**
 * Grid of Product Selection tiles for the Creation Flow
 */
class ProductSelectionTilesComponent extends React.Component<ProductSelectionTilesProps> {
  static displayName = 'ProductSelectionTiles';

  static contextTypes = {
    intl: intlShape.isRequired,
  };

  static propTypes = {
    config: PropTypes.shape({
      productSizesAvailable: validateProductSizesMap,
    }).isRequired,
    creationCategory: PropTypes.string.isRequired,
  };

  productGenerator(): Array<Element> {
    const availableSizes = this.props.config.productSizesAvailable;
    const productsToDisplay =
      this.props.creationCategory === 'create' ? getProductTypeIds() : getFullUploadProductTypeIds();

    return productsToDisplay.map((productTypeId) => {
      const productTypeName = this.context.intl.formatMessage(getProductTypeIntlPluralKey(productTypeId));
      let isDisabled = false;

      if (this.props.creationCategory === 'create') {
        // Only in creation flow we disable product tiles, as product availability depends on having templates available.
        isDisabled = !isProductTypeAvailable(productTypeId, availableSizes);
      }

      const tile = (
        <TileGridContent
          key={`product-${productTypeId}`}
          title={productTypeName}
          image={`${productTypeId}.${productTypeImageExtension}`}
          disabled={isDisabled}
        />
      );

      if (!isDisabled) {
        const productType = getProductRouteById(productTypeId);
        const productUrl = envUrl(`/products/${this.props.creationCategory}/${productType}`);
        return (
          <Link to={productUrl} key={`link-${productTypeId}`}>
            {' '}
            {tile}{' '}
          </Link>
        );
      }

      return tile;
    });
  }

  render() {
    return (
      <div className="product-selection-tiles-component">
        <TileGrid>{this.productGenerator()}</TileGrid>
      </div>
    );
  }
}

export default ProductSelectionTilesComponent;
